import { useEffect, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { api, money, catLabel, asset, timeLeft } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Gavel, Package, Plus, Trophy, CheckCircle2 } from "lucide-react";

export default function MyAuctions() {
  const { user, loading } = useAuth();
  const [data, setData] = useState({ selling: [], bidding: [] });
  const [tab, setTab] = useState("selling");
  const navigate = useNavigate();

  const load = () => api.get("/auctions/mine").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { if (user) load(); }, [user]);

  if (loading) return <div className="min-h-screen bg-[#0B0E14] text-slate-400 flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const payNow = async (a) => {
    try {
      const { data } = await api.post(`/auctions/${a.auction_id}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout");
    }
  };

  const lists = tab === "selling" ? data.selling : data.bidding;

  return (
    <div className="min-h-screen bg-[#0B0E14]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-extrabold tracking-tight text-white">My Auctions</h1>
          <Button data-testid="my-sell-btn" onClick={() => navigate("/sell")} className="bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-full h-10 px-5">
            <Plus className="h-4 w-4 mr-1" /> List something
          </Button>
        </div>

        <div className="flex gap-2 mb-6">
          <TabBtn active={tab === "selling"} onClick={() => setTab("selling")} testid="tab-selling">Selling ({data.selling.length})</TabBtn>
          <TabBtn active={tab === "bidding"} onClick={() => setTab("bidding")} testid="tab-bidding">Bidding ({data.bidding.length})</TabBtn>
        </div>

        {lists.length === 0 ? (
          <div className="text-center py-20 text-slate-500 flex flex-col items-center gap-3">
            <Package className="h-10 w-10" />
            <p>{tab === "selling" ? "You haven't listed anything yet." : "You haven't bid on anything yet."}</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="my-auctions-list">
            {lists.map((a) => {
              const t = timeLeft(a.ends_at);
              const won = a.status !== "live" && a.status !== "scheduled" && a.winner_id === user.user_id;
              const needsPay = won && (a.status === "sold") && a.payment_status === "unpaid";
              return (
                <div key={a.auction_id} className="flex items-center gap-4 rounded-xl bg-[#121721] border border-white/10 p-4">
                  <Link to={`/auction/${a.auction_id}`} className="h-16 w-16 rounded-lg overflow-hidden bg-slate-900 shrink-0 flex items-center justify-center">
                    {a.image_url ? <img src={asset(a.image_url)} alt="" className="h-full w-full object-cover" /> : <Package className="h-6 w-6 text-slate-700" />}
                  </Link>
                  <div className="min-w-0 flex-1">
                    <Link to={`/auction/${a.auction_id}`} className="text-white font-semibold hover:text-amber-400 truncate block">{a.title}</Link>
                    <div className="text-xs text-slate-500">{catLabel(a.category)} · {a.bid_count} bids</div>
                    <div className="text-sm font-mono text-amber-400 mt-0.5">{money(a.current_price)}
                      <span className="text-slate-500 text-xs ml-2">
                        {a.status === "live" ? (t.ended ? "closing…" : `${t.label} left`) : a.status}
                      </span>
                    </div>
                  </div>
                  {won && (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-300 font-semibold shrink-0"><Trophy className="h-3.5 w-3.5" /> You won</span>
                  )}
                  {a.status === "paid" && (
                    <span className="inline-flex items-center gap-1 text-xs text-green-400 font-semibold shrink-0"><CheckCircle2 className="h-3.5 w-3.5" /> Paid</span>
                  )}
                  {needsPay && (
                    <Button data-testid={`pay-${a.auction_id}`} onClick={() => payNow(a)} className="bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-full h-9 px-4 shrink-0">
                      <Gavel className="h-4 w-4 mr-1" /> Pay {money(a.final_price)}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const TabBtn = ({ active, onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`px-4 h-9 rounded-full text-sm font-medium border transition-all ${active ? "bg-amber-600 text-white border-amber-600 font-semibold" : "bg-[#121721] text-slate-300 border-white/10 hover:border-amber-500/40"}`}>
    {children}
  </button>
);
